import type { Clinic } from '@/api/clinics/clinicQueries';
import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import type { ClinicNameFormValues } from '@/validation/clinicValidator';
import { clinicNameSchema } from '@/validation/clinicValidator';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { toFormikValidate } from 'zod-formik-adapter';

export function ClinicsDialog({
  open,
  onOpenChange,
  clinic,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinic?: Clinic | null;
  onSubmit: (values: ClinicNameFormValues) => void;
  isPending?: boolean;
}) {
  const isEditing = !!clinic;

  const formik = useFormik<ClinicNameFormValues>({
    initialValues: {
      name: clinic?.name ?? '',
    },
    validate: toFormikValidate(clinicNameSchema),
    enableReinitialize: true,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Edit Clinic' : 'Add New Clinic'}
      maxWidth="4xl"
    >
      <form onSubmit={formik.handleSubmit} className="mt-4 flex flex-col gap-4">
        <Input
          id="name"
          name="name"
          label="Name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.name}
          touched={formik.touched.name}
        />

        <div className="flex items-center justify-between gap-4">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!formik.isValid || isPending}
          >
            {isPending ? 'Processing...' : isEditing ? 'Save Changes' : 'Add'}
          </Button>
          <RelationActionButton
            type="button"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </RelationActionButton>
        </div>
      </form>
    </DialogWrapper>
  );
}
